import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * Firebase Cloud Functions adapter
 * Provides easy access to callable functions
 * @returns {object} the available functions
 */
export default function functionsFns() {
  const functions = getFunctions();

  /**
   * Signs up a new user with their own organization
   * @param {object} data Function parameters
   * @param {string} data.displayName User's display name
   * @param {string} data.email User's email
   * @param {string} [data.orgName] Optional org name (defaults to displayName)
   * @returns {Promise<{success: boolean, organizationId: string, userId: string}>} The response
   */
  async function signupWithOrg(data) {
    const callable = httpsCallable(functions, "signupWithOrg");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Initializes a new user after OAuth signup (Google, etc.)
   * Gets user info from Firebase Auth automatically
   * @param {object} [data] Function parameters
   * @param {string} [data.orgName] Optional org name (defaults to user's display name)
   * @returns {Promise<{success: boolean, organizationId: string, userId: string}>} The response
   */
  async function initializeNewUser(data = {}) {
    const callable = httpsCallable(functions, "initializeNewUser");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Creates a new organization
   * @param {object} data Function parameters
   * @param {string} data.displayName Organization display name
   * @param {string} data.name Organization slug/name
   * @returns {Promise<{success: boolean, organizationId: string, teamId: string}>} The response
   */
  async function createOrganization(data) {
    const callable = httpsCallable(functions, "createOrganization");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Creates a new team in an organization
   * @param {object} data Function parameters
   * @param {string} data.name Team name
   * @param {string} data.organizationId Organization ID
   * @returns {Promise<{success: boolean, teamId: string}>} The response
   */
  async function createTeam(data) {
    const callable = httpsCallable(functions, "createTeam");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Redeems an invite to join a team
   * @param {object} data Function parameters
   * @param {string} data.token Invite token
   * @returns {Promise<{success: boolean, organizationId: string, teamId: string}>} The response
   */
  async function redeemInvite(data) {
    const callable = httpsCallable(functions, "redeemInvite");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Updates a user's role in an organization
   * @param {object} data Function parameters
   * @param {string} data.organizationId Organization ID
   * @param {string} data.targetUserId User whose role to update
   * @param {number} data.newRole New role (2=ORG_ADMIN, 3=ORG_MEMBER)
   * @returns {Promise<{success: boolean}>} The response
   */
  async function updateOrgRole(data) {
    const callable = httpsCallable(functions, "updateOrgRole");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Updates a user's role in a team
   * @param {object} data Function parameters
   * @param {string} data.teamId Team ID
   * @param {string} data.targetUserId User whose role to update
   * @param {number} data.newRole New role (4=TEAM_ADMIN, 5=TEAM_MEMBER)
   * @returns {Promise<{success: boolean}>} The response
   */
  async function updateTeamRole(data) {
    const callable = httpsCallable(functions, "updateTeamRole");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Removes a user from an organization
   * @param {object} data Function parameters
   * @param {string} data.organizationId Organization ID
   * @param {string} data.targetUserId User to remove
   * @returns {Promise<{success: boolean}>} The response
   */
  async function removeFromOrg(data) {
    const callable = httpsCallable(functions, "removeFromOrg");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Removes a user from a team
   * @param {object} data Function parameters
   * @param {string} data.teamId Team ID
   * @param {string} data.targetUserId User to remove
   * @returns {Promise<{success: boolean}>} The response
   */
  async function removeFromTeam(data) {
    const callable = httpsCallable(functions, "removeFromTeam");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Adds an existing org member to a team
   * @param {object} data Function parameters
   * @param {string} data.teamId Team ID
   * @param {string} data.targetUserId User to add
   * @returns {Promise<{success: boolean}>} The response
   */
  async function addToTeam(data) {
    const callable = httpsCallable(functions, "addToTeam");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Gets organization members with their profiles (server-side validation)
   * @param {object} data Function parameters
   * @param {string} data.organizationId Organization ID
   * @returns {Promise<{success: boolean, members: Array}>} The response
   */
  async function getOrgMembers(data) {
    const callable = httpsCallable(functions, "getOrgMembers");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Gets team members with their profiles (server-side validation)
   * @param {object} data Function parameters
   * @param {string} data.teamId Team ID
   * @returns {Promise<{success: boolean, members: Array}>} The response
   */
  async function getTeamMembers(data) {
    const callable = httpsCallable(functions, "getTeamMembers");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Gets goals for a team in a specific week (server-side validation)
   * @param {object} data Function parameters
   * @param {string} data.teamId Team ID
   * @param {string} data.organizationId Organization ID
   * @param {string} data.weekOf Week identifier (yyyy-MM-dd)
   * @param {string[]} data.userIds Array of user IDs
   * @returns {Promise<{success: boolean, goals: Array}>} The response
   */
  async function getTeamGoals(data) {
    const callable = httpsCallable(functions, "getTeamGoals");
    const result = await callable(data);
    return result.data;
  }

  /**
   * Gets goal weeks for a team (server-side validation)
   * @param {object} data Function parameters
   * @param {string} data.teamId Team ID
   * @param {string} data.organizationId Organization ID
   * @param {string} data.weekOf Week identifier (yyyy-MM-dd)
   * @param {string[]} data.userIds Array of user IDs
   * @returns {Promise<{success: boolean, goalWeeks: Array}>} The response
   */
  async function getTeamGoalWeeks(data) {
    const callable = httpsCallable(functions, "getTeamGoalWeeks");
    const result = await callable(data);
    return result.data;
  }

  return {
    signupWithOrg,
    initializeNewUser,
    createOrganization,
    createTeam,
    redeemInvite,
    updateOrgRole,
    updateTeamRole,
    removeFromOrg,
    removeFromTeam,
    addToTeam,
    getOrgMembers,
    getTeamMembers,
    getTeamGoals,
    getTeamGoalWeeks,
  };
}
